import {
  ACCOUNT_NOT_SETUP_MESSAGE,
  signOut,
  useAuthContext,
} from "@/context/auth-context";
import { useUserGroupsData } from "@/data/use-groups-data";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useGroupSlug } from "./use-group-slug";
import { useGroupUuid } from "./use-group-uuid";
import { useUserLocalSettings } from "./use-user-local-settings";

export const useRedirection = () => {
  const groupSlug = useGroupSlug(false);
  const groupUuid = useGroupUuid();
  const location = useLocation();
  const loggedIn = (location.search as Record<string, string | undefined>)
    .loggedIn;
  const { lastGroupUuid, setLastGroupUuid } = useUserLocalSettings();
  const { groups, isLoading: isLoadingGroups } = useUserGroupsData();
  const [isInitializing, setIsInitializing] = useState(true);
  const { token, authDetails, isLoading: isLoadingAuth } = useAuthContext();
  const pathname = location.pathname;
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoadingGroups || isLoadingAuth || (token && !authDetails)) {
      return;
    }

    const setAsInitialized = () => {
      if (isInitializing) {
        setIsInitializing(false);
      }
    };

    // 1. Not authenticated
    if (!token) {
      // 1.1. Public pages
      if (pathname.startsWith("/u/login")) {
        setAsInitialized();
        return;
      }

      // 1.2. Redirect to login
      if (pathname === "" || pathname === "/") {
        setAsInitialized();
        navigate({ to: "/u/login", replace: true });
        return;
      }

      // 1.3. Redirect to login with redirection
      // `redirect` is handled in ui/src/pages/LoginPage.tsx
      else {
        const encodedRedirect = encodeURIComponent(pathname);
        navigate({
          to: "/u/login",
          search: { redirect: encodedRedirect },
          replace: true,
        });
        setAsInitialized();
        return;
      }
    }

    // 2. No group, sign out
    if (groups.length === 0) {
      toast.error(ACCOUNT_NOT_SETUP_MESSAGE);
      signOut().then(() => {
        navigate({ to: "/u/login", replace: true });
        setAsInitialized();
      });
      return;
    }

    if (groupSlug || pathname.startsWith("/w")) {
      const allowedGroup = groups.find((w) => w.slug === groupSlug);

      // 3. If allowed, continue
      if (allowedGroup) {
        setLastGroupUuid(allowedGroup.uuid);
        setAsInitialized();
        return;
      }

      // 4. If not allowed, redirect to single group
      if (groups.length === 1) {
        toast.info("Redirecting to your workspace...");
        navigate({
          to: "/w/$groupSlug",
          params: { groupSlug: groups[0].slug },
          replace: true,
        });
        setAsInitialized();
        return;
      }

      // 5. If just logged in, redirect to last visited group
      if (loggedIn === "true" && lastGroupUuid) {
        const lastGroup = groups.find((w) => w.uuid === lastGroupUuid);
        if (lastGroup) {
          toast.info("Redirecting to your last visited group...");
          navigate({
            to: "/w/$groupSlug",
            params: { groupSlug: lastGroup.slug },
            replace: true,
          });
          setAsInitialized();
          return;
        }
      }

      // 6. By default, redirect to groups list
      navigate({ to: "/u/workspaces", replace: true });
      setAsInitialized();
      return;
    }

    setAsInitialized();
  }, [
    groups,
    groupUuid,
    groupSlug,
    isLoadingGroups,
    token,
    authDetails,
    isLoadingAuth,
  ]);

  return {
    isInitializing,
  };
};
